import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HistoryBaseComponent } from 'src/app/history-base/history-base.component';
import { PaymentMethod } from 'src/infrastructure/enums/paymentMethod';
import { ReturnStatus } from 'src/infrastructure/enums/returnStatus';
import { CartLine } from 'src/models/DTO/cart/cartLine';
import { CreatedOrder } from 'src/models/DTO/orders/createdOrder';
import { OrderReturnDataRequest } from 'src/models/DTO/returns/orderReturnDataRequest';
import { ToastService } from 'src/services/core/toast/toast.service';
import { ImagesService } from 'src/services/request/images/images.service';
import { OrderService } from 'src/services/request/orders/order.service';
import { PartnerService } from 'src/services/request/partner/partner.service';
import { OrderReturnServiceService } from 'src/services/request/returns/order-return-service.service';
import { StripeService } from 'src/services/request/stripe/stripe.service';
import { HistoryService } from 'src/services/state/history/history.service';

@Component({
  selector: 'app-return',
  templateUrl: './return.component.html',
  styleUrls: ['./return.component.css']
})
export class ReturnComponent extends HistoryBaseComponent implements OnInit {

  private _return?: OrderReturnDataRequest;
  private _reasons: { [key: string] : any } = {
    unpacked: "WANT_TO_RETURN_UNOPENED_PRODUCT",
    description: "PRODUCT_DIDNT_FIT_DESCRIPTION",
    nonworking: "PRODUCT_NOT_WORKING",
    delivery: "DELIVERY_TOOK_MORE_THAN_DESCRIBED",
    mistake: "ORDERED_BY_MISTAKE_OR_NOT_NEEDED_ANYMORE"
  };
  private _statuses = {
    confirmed: "confirmed",
    cancelled: "cancelled"
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private orderReturnsService: OrderReturnServiceService,
    private partnerService: PartnerService,
    private ordersService: OrderService,
    private imagesService: ImagesService,
    private stripeService: StripeService,
    private toastrService: ToastService,
    private router: Router,
    protected location: Location,
    protected historyService: HistoryService
  ) {
    super(location, historyService)
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.loadReturn(params.id);
    })
  }

  private loadReturn(returnUUID: string) {
    this.orderReturnsService.getReturnInfo(returnUUID).subscribe(returnInfo => {
      this._return = returnInfo;
      this.loadOrder(this._return);
    })
  }

  private loadOrder(returnRequest: OrderReturnDataRequest) {
    let orderUUID = returnRequest.data.order.uuid
    this.ordersService.getOrder(orderUUID).subscribe(order => {
      returnRequest.data.fullOrder = order;
      this.partnerService.getPartnerInfo(order.data.lines[0].sku.product.organisation.uuid).subscribe(partner => {
        returnRequest.data.organisation = partner;
      })
      if (!this.isCOD(order)) {
        if (order.data.payment_method_charge_id) {
          this.stripeService.getPaymentIntentDetails(order.data.payment_method_charge_id).subscribe(paymentIntent => {
            returnRequest.data.paymentIntent = paymentIntent;
          })
        }

      }
      order.data.lines.findIndex(product => {
        this.imagesService.getImagesForProduct("SKU", product.sku.uuid).subscribe(image => {
          if (image.data.length) {
            product.imageSource = image.data[0]?.original.url || '/assets/images/shared/logo.png';
          }
          else {
            this.imagesService.getImagesForProduct("PRODUCT", product.sku.product.uuid).subscribe(image => {
              product.imageSource = image.data[0]?.original.url || '/assets/images/shared/logo.png';
            })
          }
        })
      })
    })
  }

  public isCOD(order: CreatedOrder) {
    return order.data.paymentMethod.code == PaymentMethod.COD
  }

  get order() {
    return this._return?.data.fullOrder
  }

  get cardImg() {
    if (!this._return?.data.paymentIntent?.charges.data[0].payment_method_details.card.brand) {
      return '';
    }
    let cardImage = '/assets/images/cards/card-mastercard.svg';
    if (this._return?.data.paymentIntent?.charges.data[0].payment_method_details.card.brand == "visa") {
      cardImage = '/assets/images/cards/card-visa.svg';
    }
    return cardImage
  }

  get getCardDetail() {
    if (!this._return?.data.paymentIntent?.charges.data[0].payment_method_details.card.brand) {
      return '-';
    }
    let cardName = "MASTERCARD *";
    if (this._return?.data.paymentIntent?.charges.data[0].payment_method_details.card.brand == "visa") {
      cardName = "VISA DEBIT *";
    }
    return cardName + this._return?.data.paymentIntent?.charges.data[0].payment_method_details.card.last4
  }

  get organisation() {
    return this._return?.data.organisation
  }

  get request() {
    return this._return?.data
  }

  get return() {
    return this._return;
  }

  get streetInfo() {
    if (!this.organisation?.data) return '';
    let streetInfo = '';
    streetInfo += this.organisation.data.return_address.street_name;
    if (this.organisation.data.return_address.street_number) {
      streetInfo += (' ' + this.organisation.data.return_address.street_number + ', ');
    }
    if (this.organisation.data.return_address.district) {
      streetInfo += this.organisation.data.return_address.district;
    }
    if (this.organisation.data.return_address.address_line_2) {
      streetInfo += (' ' + this.organisation.data.return_address.address_line_2);
    }
    return streetInfo;
  }

  public date(date: any) {
    return new Date(date).toLocaleDateString() || ''
  }

  public time(date: any) {
    return new Date(date).toLocaleTimeString() || '';
  }

  public isCartReturned(cartUUID: string) {
    if (this._return?.data.items.find(item => item.order_line.uuid == cartUUID)) {
      return true;
    }
    return false;
  }

  getStatusClass(status: string) {
    switch (status) {
      case this._statuses.cancelled:
        return "cancel"
      case this._statuses.confirmed:
        return "success"
      default:
        return ""
    }
  }

  toFixed(to: number, number: string) {
    let convertedNumber = Number.parseFloat(number);
    if (!convertedNumber) return '-';
    return convertedNumber.toFixed(to) + ' лв.';
  }

  returnState(state: string) {
    return ReturnStatus[state];
  }

  returnReason(reason: string) {
    return this._reasons[reason];
  }

  pricingInfo(cartLine: CartLine, orderreturnRequest: OrderReturnDataRequest, cartLineUUID: string) {
    let price = cartLine.price_per_piece;
    let priceAsString =  Number.parseFloat(price).toFixed(2);
    return   this.returnReturnQuantityForProduct(orderreturnRequest, cartLineUUID) + ' x ' + priceAsString + ' лв.';
  }

  getUnitPriceBase(cartLine: CartLine): number {
    return Number(cartLine.price_per_piece) || 0;
  }

  getLineTotalBase(cartLine: CartLine, orderReturnRequest: OrderReturnDataRequest, cartLineUUID: string): number {
    return this.getUnitPriceBase(cartLine) * this.returnReturnQuantityForProduct(orderReturnRequest, cartLineUUID);
  }

  pricingInfoText(orderReturnRequest: OrderReturnDataRequest, cartLineUUID: string): string {
    var qty = this.returnReturnQuantityForProduct(orderReturnRequest, cartLineUUID);
    return qty + ' x';
  }

  getRefundedAmountBase(): number {
    return Number(this.request?.amount_refunded) || 0;
  }

  getPriceAsFixedString(cartLine: CartLine, orderreturnRequest: OrderReturnDataRequest, cartLineUUID: string) {
    if (!cartLine.price_per_piece) return '00.00';
    let priceCalculated =  Number.parseFloat(cartLine.price_per_piece) * this.returnReturnQuantityForProduct(orderreturnRequest, cartLineUUID)
    let fixedPrice = priceCalculated.toFixed(2);
    let priceArray = fixedPrice.split('.');
    priceArray[0] = priceArray[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return priceArray;
  }

  getTotalPriceAsFixedString(price: string) {
    if (!price) return '00.00';
    let fixedPrice = Number.parseFloat(price).toFixed(2);
    let priceArray = fixedPrice.split('.');
    priceArray[0] = priceArray[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return priceArray[0] + '.' +priceArray[1];
  }

  goToProduct(productPerOrg: CartLine) {
    if (productPerOrg.sku.product.deleted_at) {
      this.toastrService.info("Продуктът вече не е наличен.");
      return;
    }
    this.router.navigate(['product/' + productPerOrg.sku.product.uuid + '/' + 'none'])
  }

  returnOptionValue(option: any) {
    if (option.option.values.length) {
      let optionSelected = option.option.values.find((value: { uuid: any; }) => value.uuid == option.value);
      return optionSelected.value;
    }
    return option.value;
  }

  returnReturnQuantityForProduct(orderreturnRequest: OrderReturnDataRequest, cartLineUUID: string) {
    const item = orderreturnRequest.data.items.find(item => item.order_line.uuid == cartLineUUID);
    return item?.quantity || 0;
  }
}
